/**
 * saas-auth product-admin identity client (TMMPDM-0004). TMMPDM-0017/0020/0021 (MM-D-101): the
 * roster, detail, reseller assignment and tenant create moved to tmm-api (`../tmm/admin.ts`).
 * This module keeps only member access and invitations.
 *
 * Surface: the bootstrap-resolved AUTH origin, `/api/product-admin/**` — already
 * bearer-attached, CSRF-protected (cookie-echo: `X-CSRF-Token` header + `csrfToken`
 * body echo on writes) and credentialed by `src/api/client.ts`'s predicates, so
 * nothing extra happens here. Every response is the `{success,message,data}`
 * ApiResponse envelope, so every call `unwrap()`s it. Tenant ids travel only as
 * the addressed resource in the path; saas-auth ownership-checks them to this
 * product (404 outside it). Product is never on the wire.
 */
import { api } from '../client';
import type { ApiResponse, PagedResponse } from '../types';
import type {
  AddMemberRequest,
  AddMemberResult,
  AssignableRole,
  InvitationStatus,
  TenantInvitationDto,
  TenantMemberDto,
  TenantMemberStatus,
} from './types';

const BASE = '/api/product-admin/tenants';

function unwrap<T>(envelope: ApiResponse<T>): T {
  return envelope.data;
}

export interface ListMembersQuery {
  page?: number;
  size?: number;
  q?: string;
}

/**
 * `GET /api/product-admin/tenants/{tenantId}/users` — the tenant's CUSTOMER list
 * (SASATH-0319). `GUEST` members are filtered out of `content`;
 * `totalElements`/`totalPages` pass through unfiltered. Staff come from
 * `listStaff` — never point this at `/staff`.
 */
export async function listMembers(
  tenantId: number,
  query?: ListMembersQuery,
): Promise<PagedResponse<TenantMemberDto>> {
  const { data } = await api.get<ApiResponse<PagedResponse<TenantMemberDto>>>(
    `${BASE}/${tenantId}/users`,
    { params: query },
  );
  const page = unwrap(data);
  return { ...page, content: page.content.filter((m) => m.role !== 'GUEST') };
}

/**
 * `GET /api/product-admin/tenants/{tenantId}/staff` — the tenant's staff ladder
 * (OWNER | ADMIN | STAFF; SASATH-0326). Same paging query as `listMembers`. No
 * GUEST filter — staff rows are never GUEST. Fail closed on 404; never fall
 * back to `GET …/users`.
 */
export async function listStaff(
  tenantId: number,
  query?: ListMembersQuery,
): Promise<PagedResponse<TenantMemberDto>> {
  const { data } = await api.get<ApiResponse<PagedResponse<TenantMemberDto>>>(
    `${BASE}/${tenantId}/staff`,
    { params: query },
  );
  return unwrap(data);
}

/**
 * `POST /api/product-admin/tenants/{tenantId}/users` — discriminated by response
 * SHAPE, not HTTP status: a `membershipId` field means an existing account was
 * added directly as a member (201); an `expiresAt` field means an unknown email
 * was invited instead (202, and appears in the invitations list). The two are
 * mutually exclusive and always present on their DTOs.
 */
export async function addMember(
  tenantId: number,
  body: AddMemberRequest,
): Promise<AddMemberResult> {
  const { data } = await api.post<ApiResponse<TenantMemberDto | TenantInvitationDto>>(
    `${BASE}/${tenantId}/users`,
    body,
  );
  const result = unwrap(data);
  if ('membershipId' in result) return { kind: 'member', member: result };
  return { kind: 'invitation', invitation: result as TenantInvitationDto };
}

/** `PUT /api/product-admin/tenants/{tenantId}/users/{membershipId}/role`. */
export async function changeRole(
  tenantId: number,
  membershipId: number,
  role: AssignableRole,
): Promise<TenantMemberDto> {
  const { data } = await api.put<ApiResponse<TenantMemberDto>>(
    `${BASE}/${tenantId}/users/${membershipId}/role`,
    { role },
  );
  return unwrap(data);
}

/** `PUT /api/product-admin/tenants/{tenantId}/users/{membershipId}/status` — `DISABLED` blocks login. */
export async function setMemberStatus(
  tenantId: number,
  membershipId: number,
  status: TenantMemberStatus,
): Promise<TenantMemberDto> {
  const { data } = await api.put<ApiResponse<TenantMemberDto>>(
    `${BASE}/${tenantId}/users/${membershipId}/status`,
    { status },
  );
  return unwrap(data);
}

/** `DELETE /api/product-admin/tenants/{tenantId}/users/{membershipId}` — deletes the membership, not the account. */
export async function removeMember(tenantId: number, membershipId: number): Promise<void> {
  await api.delete<ApiResponse<unknown>>(`${BASE}/${tenantId}/users/${membershipId}`);
}

/** `GET /api/product-admin/tenants/{tenantId}/invitations?status=` — a JSON array, not paged; newest first. */
export async function listInvitations(
  tenantId: number,
  status?: InvitationStatus,
): Promise<TenantInvitationDto[]> {
  const { data } = await api.get<ApiResponse<TenantInvitationDto[]>>(
    `${BASE}/${tenantId}/invitations`,
    status ? { params: { status } } : undefined,
  );
  return unwrap(data);
}

/** `POST …/invitations/{invitationId}/resend` — rotates + re-sends; no token ever returned. */
export async function resendInvitation(
  tenantId: number,
  invitationId: number,
): Promise<TenantInvitationDto> {
  const { data } = await api.post<ApiResponse<TenantInvitationDto>>(
    `${BASE}/${tenantId}/invitations/${invitationId}/resend`,
    {},
  );
  return unwrap(data);
}

/** `DELETE …/invitations/{invitationId}` — idempotent on an already-revoked invite. */
export async function revokeInvitation(
  tenantId: number,
  invitationId: number,
): Promise<TenantInvitationDto> {
  const { data } = await api.delete<ApiResponse<TenantInvitationDto>>(
    `${BASE}/${tenantId}/invitations/${invitationId}`,
  );
  return unwrap(data);
}
